import DatePicker from '@/components/Common/DatePicker/DatePicker';
import FormInput from '@/components/Common/FormInput/FormInput';
import { Layout } from '@/components/Common/Layout';
import PatientSelect from '@/components/Common/PatientSelect/PatientSelect';
import Stepper from '@/components/Common/Stepper/Stepper';
import { useAppContext } from '@/context/AppContext';
import { IClaimCreateStepProps } from '@/pages';
import { getApplicableBenefits } from '@/queries/memberDetails.queries';
import useClaimCreateStore, {
  CLAIM_REASON_MAX_LENGTH,
  ClaimCreateFormSchema,
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import {
  EBenefitCategory,
  ECareType,
  EClaimType,
  EEnrollmentStatus,
  EPatientRelationship,
  IFormSchema,
} from '@/types';
import { getDimensions, getPatientDetails } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Button } from 'ui/mobile-ds';
import { z } from 'zod';

const Schema = ClaimCreateFormSchema.pick({
  reasonForHospitalization: true,
  date: true,
  patientId: true,
  patientName: true,
  patientRelationship: true,
  organisationBenefitId: true,
  insuranceProviderHandle: true,
  tpaName: true,
  dateFieldName: true,
});

type TSchema = z.infer<typeof Schema>;

const TreatmentDetails = ({ submitFlow }: IClaimCreateStepProps) => {
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const setFormState = useClaimCreateStore((state) => state.setFormState);
  const formState = useClaimCreateStore((state) => state.formState);

  const {
    register,
    handleSubmit,
    // formState: { isValid },
    watch,
    setValue,
    getValues,
  } = useForm<TSchema>({
    resolver: zodResolver(Schema),
    defaultValues: {
      date: formState?.date,
      patientId: formState?.patientId,
      patientName: formState?.patientName,
      patientRelationship: formState?.patientRelationship,
      reasonForHospitalization: formState?.reasonForHospitalization,
      dateFieldName: formState?.dateFieldName,
    },
  });

  const [dateSchema, setDateSchema] = useState<IFormSchema | null>(null);

  const watchDate = watch('date');
  const watchPatientId = watch('patientId');
  const watchReason = watch('reasonForHospitalization') ?? '';

  const {
    refetch: refetchApplicableBenefits,
    isFetching: isFetchingApplicableBenefits,
  } = useQuery({
    ...getApplicableBenefits({
      typeOfClaim: formState.typeOfClaim as EClaimType,
      typeOfCare: ECareType.OPD,
      patientId: watchPatientId ?? '',
      date: new Date(watchDate ?? ''),
      benefitCategory: [EBenefitCategory.EXPENSE_MANAGEMENT],
    }),
    enabled: false,
  });

  const onSubmit: SubmitHandler<TSchema> = async (data) => {
    const extraFields: Partial<TSchema> = {};

    const { data: applicableBenefits, isError: isApplicableBenefitsError } =
      await refetchApplicableBenefits();

    if (isApplicableBenefitsError) {
      toast.error('Oops! Something went wrong. Please try again later.', {
        id: 'create-claim-error',
        style: {
          marginBottom: getDimensions('claims-footer').height,
        },
      });
      return;
    }

    extraFields.organisationBenefitId =
      applicableBenefits?.[0]?.orgBenefitId ?? null;
    extraFields.insuranceProviderHandle =
      applicableBenefits?.[0].insuranceProviderHandle ?? null;
    extraFields.tpaName = applicableBenefits?.[0].tpaName ?? null;
    setValue('organisationBenefitId', extraFields.organisationBenefitId);
    setValue('insuranceProviderHandle', extraFields.insuranceProviderHandle);
    setValue('tpaName', extraFields.tpaName);
    setFormState({ ...data, ...extraFields });

    submitFlow?.({
      ...formState,
      ...data,
      ...extraFields,
    });
  };

  const onError: SubmitErrorHandler<TSchema> = (error) => {
    window.dev.e({
      error,
      formState,
      values: getValues(),
    });
    const reasonError = error.reasonForHospitalization;
    toast.error(
      reasonError?.type === 'too_big' && reasonError.message
        ? reasonError.message
        : 'Please fill all the mandatory fields',
      {
        id: 'create-claim-error',
        style: {
          marginBottom: getDimensions('claims-footer').height,
        },
      },
    );
  };

  const dateInputValue = watchDate
    ? format(new Date(watchDate ?? ''), 'dd-MMM-yyyy')
    : '';

  const { userConfig } = useAppContext();

  const isUnderEnrolment =
    userConfig?.enrollmentStatus &&
    [EEnrollmentStatus.INCOMPLETE, EEnrollmentStatus.ENROLLED].includes(
      userConfig?.enrollmentStatus,
    );

  return (
    <Layout.Main
      headerProps={{
        title: 'Fill in the details below',
        description: 'Enter all the details about your claim below',
        onBack: () => {
          if (isUnderEnrolment) {
            setCurrentStep(EClaimCreateFlowSteps.ORG_UNDER_ENROLLMENT);
          } else {
            setCurrentStep(EClaimCreateFlowSteps.SELECT_CLAIM_TYPE);
          }
        },
        children: (
          <div className="flex justify-between md:hidden">
            <h4 className="text-sm text-mobile-hero-100 font-medium leading-5">
              Basic Information
            </h4>
            <span className="text-sm leading-5 text-mobile-text-hierarchy-2">
              Step 1/3
            </span>
          </div>
        ),
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        className="flex flex-1 flex-col h-full pb-24"
      >
        <section className="w-full bg-white p-8 border border-secondary-100 rounded-2xl flex flex-col md:grid md:grid-cols-2 gap-6">
          <PatientSelect
            value={watchPatientId}
            onChange={(patient) => {
              const { fullName, id } = getPatientDetails(patient ?? {});
              setValue('patientName', fullName);
              setValue(
                'patientRelationship',
                patient?.relationship ?? EPatientRelationship.SELF,
              );
              setValue('patientId', id ?? '');
              setDateSchema(
                patient?.featureCategories?.opd?.formSchema ?? null,
              );
            }}
          />
          <FormInput
            label="Reason for expense"
            helper={{
              text: `${watchReason.length}/${CLAIM_REASON_MAX_LENGTH}`,
            }}
            inputProps={{
              required: true,
              placeholder: 'Enter the reason',
              maxLength: CLAIM_REASON_MAX_LENGTH,
              ...register('reasonForHospitalization', {
                required: true,
              }),
            }}
          />
          <DatePicker
            disabled={!watchPatientId}
            placeholder={'Select a date'}
            popperOptions={{
              placement: 'right-end',
              strategy: 'fixed',
            }}
            inputProps={{
              label: 'Date of expense',
            }}
            value={dateInputValue}
            // @ts-ignore
            dayPickerProps={{
              required: true,
              selected: new Date(watchDate ?? ''),
              onSelect: (selectedDate: Date) => {
                setValue('date', selectedDate?.toISOString());
                setValue('dateFieldName', dateSchema?.start_date?.field ?? '');
              },
              ...(dateSchema && {
                modifiers: {
                  disabled: {
                    before: dateSchema?.start_date?.lower_limit
                      ? new Date(dateSchema?.start_date?.lower_limit)
                      : undefined,
                    after: dateSchema?.start_date?.upper_limit
                      ? new Date(dateSchema?.start_date?.upper_limit)
                      : undefined,
                  },
                },
              }),
            }}
            {...register('date', {
              ...(dateSchema?.start_date?.lower_limit && {
                min: dateSchema.start_date.lower_limit,
              }),
              ...(dateSchema?.start_date?.upper_limit && {
                max: dateSchema.start_date.upper_limit,
              }),
            })}
            onDisabledDateClick={() => {
              toast.error('Please select a date within your policy period', {
                id: 'create-claim-error',
                style: {
                  marginBottom: getDimensions('claims-footer').height,
                },
              });
            }}
          />
        </section>
        <Layout.Footer isSticky containerClassName="flex justify-between">
          <Stepper
            className="hidden md:flex"
            activeStep={1}
            activeSubStep={0}
            totalSubSteps={0}
            totalSteps={3}
            title="Basic information"
          />
          <Button
            type="submit"
            className="ml-auto md:max-w-[260px] w-full text-base"
            loading={isFetchingApplicableBenefits}
          >
            Continue
          </Button>
        </Layout.Footer>
      </form>
    </Layout.Main>
  );
};

export default TreatmentDetails;
